import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import Drawer from "../../layouts/Drawer";
import LoadingWheel from "../shared/LoadingWheel";
import Avatar from "../user/Avatar";
import usePartTimeline, {
  IPartTimelineEvent,
  IPartTimelineOffer,
  PartTimelineSource,
} from "../../data/usePartTimeline";
import { useUserLookup } from "../../hooks/useUserLookup";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";

interface PartHistoryDrawerProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  partId: string;
}

const sourceLabels: Record<PartTimelineSource, string> = {
  [PartTimelineSource.Part]: "Part",
  [PartTimelineSource.Offer]: "Listing",
  [PartTimelineSource.OrderHistory]: "Order",
};

const sourceColours: Record<PartTimelineSource, string> = {
  [PartTimelineSource.Part]: "bg-slate-100 text-slate-700",
  [PartTimelineSource.Offer]: "bg-indigo-100 text-indigo-700",
  [PartTimelineSource.OrderHistory]: "bg-emerald-100 text-emerald-700",
};

function OfferSummary({ offer }: { offer: IPartTimelineOffer }) {
  const { t } = useTranslation("stock");

  return (
    <div
      className={classNames(
        "rounded-md border px-3 py-2 text-xs",
        offer.isActive ? "border-primary-200 bg-primary-50/50" : "border-gray-100 bg-white"
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-gray-700">{offer.sku || t("No SKU")}</span>
        {offer.isActive && (
          <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-medium text-primary-700">
            {t("Active offer")}
          </span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-gray-500">
        <span>{t("Item")}: {offer.ebayId || "-"}</span>
        {offer.listingStarted && (
          <span>{t("Started")}: {formatDateTime(offer.listingStarted, DateTime.DATE_SHORT)}</span>
        )}
        {offer.listingEnded && (
          <span>{t("Ended")}: {formatDateTime(offer.listingEnded, DateTime.DATE_SHORT)}</span>
        )}
        {!offer.publishedOn && <span className="italic">{t("Not published")}</span>}
      </div>
    </div>
  );
}

function TimelineEvent({
  timelineEvent,
  userName,
}: {
  timelineEvent: IPartTimelineEvent;
  userName: string | undefined;
}) {
  const { t } = useTranslation("stock");
  const [expanded, setExpanded] = useState(false);
  const hasChanges = timelineEvent.changes.length > 0;

  return (
    <div
      className={classNames(
        "border-b border-gray-100 py-3",
        hasChanges ? "cursor-pointer hover:bg-gray-50" : ""
      )}
      onClick={() => hasChanges && setExpanded(!expanded)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={classNames(
              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
              sourceColours[timelineEvent.source]
            )}
          >
            {t(sourceLabels[timelineEvent.source])}
          </span>
          {timelineEvent.isActiveOffer && (
            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-medium text-primary-700">
              {t("Active offer")}
            </span>
          )}
          {timelineEvent.source === PartTimelineSource.Offer && !timelineEvent.isActiveOffer && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
              {t("Old offer")}
            </span>
          )}
        </div>
        <span className="whitespace-nowrap text-xs text-gray-500">
          {formatDateTime(timelineEvent.timestamp, DateTime.DATETIME_MED_WITH_SECONDS)}
        </span>
      </div>

      {timelineEvent.milestones.length > 0 ? (
        <ul className="mt-1.5 space-y-0.5">
          {timelineEvent.milestones.map((milestone, index) => (
            <li key={`${timelineEvent.id}-${index}`} className="text-sm font-medium text-gray-800">
              {milestone}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1.5 text-sm text-gray-500">
          {t("{{count}} field(s) changed", { count: timelineEvent.changes.length })}
        </p>
      )}

      {timelineEvent.orderNumber && (
        <p className="mt-0.5 text-xs text-gray-500">
          {t("Order number")}: {timelineEvent.orderNumber}
        </p>
      )}

      <div className="mt-1 flex items-center gap-1.5">
        {timelineEvent.userId ? (
          <>
            <Avatar userId={timelineEvent.userId} classes="w-5 h-5" textClasses="!text-[10px]" />
            <span className="text-xs text-gray-500">{userName || t("Unknown")}</span>
          </>
        ) : (
          <span className="text-xs italic text-gray-400">{t("Sync / System")}</span>
        )}
        {hasChanges && (
          <span className="ml-1 text-xs text-gray-400">{expanded ? "▼" : "▶"}</span>
        )}
      </div>

      {expanded && hasChanges && (
        <div className="mt-2 space-y-1">
          {timelineEvent.changes.map((change) => (
            <div key={`${timelineEvent.id}-${change.field}`} className="text-sm">
              <span className="font-medium text-gray-700">{change.field}</span>
              <div className="ml-3 text-xs text-gray-500">
                <span className="text-red-500 line-through">{change.old || <em>{t("empty")}</em>}</span>
                {" → "}
                <span className="text-green-600">{change.new || <em>{t("empty")}</em>}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PartHistoryDrawer({ open, setOpen, partId }: PartHistoryDrawerProps) {
  const { t } = useTranslation("stock");
  const { partTimeline } = usePartTimeline(open ? partId : undefined, "PartHistoryDrawer");
  const { findUser } = useUserLookup();
  const [keyEventsOnly, setKeyEventsOnly] = useState(true);

  const timeline = partTimeline.data?.data;

  const events = useMemo(() => {
    const allEvents = timeline?.events ?? [];
    return keyEventsOnly ? allEvents.filter((x) => x.isKeyEvent) : allEvents;
  }, [timeline?.events, keyEventsOnly]);

  return (
    <Drawer open={open} setOpen={setOpen} title={t("Part History")} maxWidth="max-w-xl">
      {partTimeline.isLoading ? (
        <LoadingWheel />
      ) : !timeline ? (
        <p className="py-4 text-sm text-gray-500">{t("Unable to load this part's history.")}</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            <div className="rounded-lg bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                {t("Sales on record")}
              </p>
              <p className="text-base font-semibold tabular-nums text-gray-900">{timeline.saleCount}</p>
            </div>
            <div className="rounded-lg bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                {t("Returns")}
              </p>
              <p className="text-base font-semibold tabular-nums text-gray-900">{timeline.returnCount}</p>
            </div>
          </div>

          {timeline.soldMoreThanOnce && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t("This part has more than one sale on record. Check the orders below are a sale and a return, not the same part sold twice.")}
            </div>
          )}

          <div className="rounded-md border border-gray-100 bg-white p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("eBay ids")}
            </p>
            <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <dt className="text-gray-500">{t("Item number")}</dt>
              <dd className="text-gray-800">{timeline.ebayIdentity.ebayItemNumber || "-"}</dd>
              <dt className="text-gray-500">{t("Trading item id")}</dt>
              <dd className="text-gray-800">{timeline.ebayIdentity.ebayTradingApiItemId || "-"}</dd>
              <dt className="text-gray-500">{t("Inventory offer id")}</dt>
              <dd className="text-gray-800">{timeline.ebayIdentity.ebayInventoryApiOfferId || "-"}</dd>
              <dt className="text-gray-500">{t("Active offer item")}</dt>
              <dd className="text-gray-800">{timeline.ebayIdentity.activeOfferEbayId || "-"}</dd>
            </dl>
            {timeline.ebayIdentity.itemIdMismatch && (
              <p className="mt-2 text-xs text-amber-700">
                {t("The active offer and the part's trading item id point at different listings. Normal after a relist - but if both are still live on eBay the part can sell twice.")}
              </p>
            )}
          </div>

          {timeline.offers.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t("Offers")} ({timeline.offers.length})
              </p>
              {timeline.offers.map((offer) => (
                <OfferSummary key={offer.id} offer={offer} />
              ))}
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t("Timeline")}
            </p>
            <button
              type="button"
              onClick={() => setKeyEventsOnly(!keyEventsOnly)}
              className="text-xs font-medium text-primary-700 hover:text-primary-800"
            >
              {keyEventsOnly ? t("Show all changes") : t("Key events only")}
            </button>
          </div>

          {events.length === 0 ? (
            <p className="py-4 text-sm text-gray-500">{t("No history recorded for this part.")}</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {events.map((timelineEvent) => (
                <TimelineEvent
                  key={timelineEvent.id}
                  timelineEvent={timelineEvent}
                  userName={
                    timelineEvent.userId ? findUser(timelineEvent.userId)?.fullname : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
